import CollapsibleSection from "components/Section";
import { Button } from "components/Button";
import React, { useCallback, useEffect, useRef, useState } from "react";
import TextValue from "components/TitleValue";
import { useTranslation } from "react-i18next";
import {
  TSubscriberWaterMeter,
  TWaterMeter,
  WaterMeterLocationType,
  WaterMeterModelType,
  WaterMeterType,
} from "types/dto";

import { netCall } from "utils/netCall";
import { ManagementApiUrl } from "environment/routeSettings";
import { toast } from "react-toastify";
import { Grid } from "components/Grid";
import { useGridDetailsContextMenu } from "components/Grid/hooks/useGridDetailsContextMenu";
import { format } from "date-fns-jalali";
import { TResponseCode } from "utils/netCall/types";
import { Permission } from "components/Permission";
import { getJalaliDate } from "utils/dateTimeTools";

type TProps = {
  waterMeterId?: string;
  historicalWaterMetersData?: TSubscriberWaterMeter[];
  onAssignMeterClick: () => void;
  onUnAssignMeterClick: () => void;
};
export const WaterMeterInfo = (props: TProps) => {
  const {
    waterMeterId,
    historicalWaterMetersData,
    onAssignMeterClick,
    onUnAssignMeterClick,
  } = props;

  const { t } = useTranslation();

  // Rows are TSubscriberWaterMeter, which carries BOTH `id` (the installation
  // record) and `waterMeterId`. The meter detail route needs `waterMeterId` —
  // `id` would resolve to a meter that does not exist.
  const meterDetailsContextMenu = useGridDetailsContextMenu((row) =>
    row?.waterMeterId
      ? `/dashboard/meters/details/${row.waterMeterId}`
      : undefined
  );

  const [data, setData] = useState<TWaterMeter>();
  const [isGettingData, setIsGettingData] = useState<boolean>(false);

  const gridRef = useRef(null);

  const getMeterDetails = async (waterMeterId: string) => {
    setIsGettingData(true);
    const res = await netCall<TWaterMeter>({
      method: "POST",
      endPoint: `${ManagementApiUrl()}/api/WaterMeter/GetWaterMeter`,
      body: {
        waterMeterId: waterMeterId,
        startDT: new Date().toISOString().split("T")[0],
        endDT: new Date().toISOString().split("T")[0],
      },
    });
    if (res.responseCode === TResponseCode.OK) {
      const record = res.results;
      setData(record);
    } else {
      toast.error(t("errorInGettingData"));
    }
    setIsGettingData(false);
  };

  useEffect(() => {
    if (waterMeterId) {
      getMeterDetails(waterMeterId);
    }
  }, [waterMeterId]);

  const renderHistoricalWaterMeters = useCallback(() => {
    if (historicalWaterMetersData) {
      return (
        <>
          <p className={"text-sm text-gray-90 font-bold mt-4"}>
            {t("historicalWaterMetersInfo")}:
          </p>
          <Grid
            ref={gridRef}
            gridKey={"id"}
            rowContextMenu={meterDetailsContextMenu}
            dataSource={historicalWaterMetersData}
            columns={[
              {
                dataField: "id",
                caption: t("id"),
              },
              {
                dataField: "serialNo",
                caption: t("serialNo"),
              },
              {
                dataField: "installationDT",
                caption: t("installationDT"),
                dataType: "date",
                calculateCellValue: (data: any) => {
                  return getJalaliDate({
                    date: data?.installationDT,
                  });
                },
              },
              {
                dataField: "unInstallationDT",
                caption: t("unInstallationDT"),
                dataType: "date",
                calculateCellValue: (data: any) => {
                  return getJalaliDate({
                    date: data?.unInstallationDT,
                  });
                },
              },
            ]}
            hasSearchPanel={false}
            hasPagination={false}
            hasGroupingFeatures={false}
            hasFilterBuilder={false}
          />
        </>
      );
    }
  }, [historicalWaterMetersData]);

  return (
    <CollapsibleSection
      title={t("waterMeterInfo")}
      collapsible={true}
      isLoading={isGettingData}
      skeleton="text"
    >
      <div className={"grid grid-cols-2 gap-y-3 gap-x-6 w-full"}>
        {!waterMeterId ? (
          <div className="w-full col-span-2 flex items-center justify-between">
            <span className="text-sm text-error-80">
              {t("noCurrentWaterMeterAssigned")}
            </span>
            <Button variant="outlined" onClick={onAssignMeterClick}>
              {t("assignMeter")}
            </Button>
          </div>
        ) : (
          <>
            <span className="text-sm text-gray-90 font-bold col-span-2">
              {t("currentWaterMeterInfo")}:
            </span>
            <TextValue
              title={t("id")}
              value={data?.id}
              className={"col-span-1"}
            />
            <TextValue
              title={t("modelType")}
              value={data?.modelType && t(WaterMeterModelType[data?.modelType])}
              className={"col-span-1"}
            />
            <TextValue
              title={t("waterMeterType")}
              value={
                data?.waterMeterType &&
                t(`enum-${WaterMeterType[data?.waterMeterType]}`)
              }
              className={"col-span-1"}
            />
            <TextValue
              title={t("locationType")}
              value={
                data?.locationType &&
                t(`enum-${WaterMeterLocationType[data?.locationType]}`)
              }
              className={"col-span-1"}
            />
            <TextValue
              title={t("productionYear")}
              value={data?.productionYear}
              className={"col-span-1"}
            />
            <TextValue
              title={t("serialNo")}
              value={data?.serialNo}
              className={"col-span-1"}
            />
            <div className="w-full col-span-2 flex items-center justify-end gap-x-2">
              <Permission allowed={["superadmin", "d_watersubscriber"]}>
                <Button
                  variant="outlined"
                  className={"text-error-80 border-error-80"}
                  onClick={onUnAssignMeterClick}
                >
                  {t("unassignMeter")}
                </Button>
              </Permission>
              <Permission allowed={["superadmin", "d_watersubscriber"]}>
                <Button variant="outlined" onClick={onAssignMeterClick}>
                  {t("assignAnotherMeter")}
                </Button>
              </Permission>
            </div>
          </>
        )}
      </div>
      {renderHistoricalWaterMeters()}
    </CollapsibleSection>
  );
};
